import React, { useState, useRef, useEffect } from "react";
import { Upload } from "lucide-react";
import { useOrganizationList } from "@wacht/tanstack-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface CreateOrganizationFormProps {
  onSuccess?: (organization?: any) => void;
  onCancel?: () => void;
  isOpen: boolean;
  onClose: () => void;
}

export const CreateOrganizationForm: React.FC<CreateOrganizationFormProps> = ({
  onSuccess,
  onCancel,
  isOpen,
  onClose,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { createOrganization } = useOrganizationList();

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      if (file.size > 2 * 1024 * 1024) {
        toast.error("File size cannot exceed 2MB");
        return;
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const getInitials = (text: string) => {
    return (
      text
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "O"
    );
  };

  const sanitizeText = (text: string): string => {
    return text.trim().replace(/[<>\"'&]/g, "");
  };

  const validateOrganizationName = (name: string): boolean => {
    return (
      name.length >= 2 &&
      name.length <= 100 &&
      /^[a-zA-Z0-9\s_.-]+$/.test(name)
    );
  };

  const handleSubmit = async () => {
    const sanitizedName = sanitizeText(name);
    const sanitizedDescription = sanitizeText(description);

    if (!sanitizedName) {
      toast.error("Please enter an organization name");
      return;
    }

    if (!validateOrganizationName(sanitizedName)) {
      toast.error(
        "Organization name must be 2-100 characters and contain only letters, numbers, spaces, dots, underscores, and hyphens",
      );
      return;
    }

    if (sanitizedDescription.length > 500) {
      toast.error("Description must be less than 500 characters");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createOrganization!({
        name: sanitizedName,
        image: image || undefined,
        description: sanitizedDescription || undefined,
      });
      // Extract organization from response (response might be { data: { organization: {...} } } or just { organization: {...} })
      const createdOrganization =
        (response as any)?.data?.organization ||
        (response as any)?.organization ||
        response;
      toast.success("Organization created successfully");
      onSuccess?.(createdOrganization);
      handleClose();
    } catch (error: any) {
      const errorMessage =
        error.message || "Failed to create organization. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName("");
    setDescription("");
    setImage(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    onClose();
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setName("");
      setDescription("");
      setImage(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }
  }, [isOpen, previewUrl]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-3xl p-0" showCloseButton={false}>
        <div className="grid md:grid-cols-3 gap-0">
          {/* Left Panel - Image Upload */}
          <div className="md:col-span-1 bg-muted/30 p-6 flex flex-col items-center justify-center border-r">
            <div
              className={cn(
                "relative w-32 h-32 rounded-lg border-2 border-dashed cursor-pointer group transition-colors",
                previewUrl ? "border-transparent" : "bg-background",
              )}
              onClick={handleFileSelect}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Organization logo"
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground text-2xl font-semibold">
                  {getInitials(name || "O")}
                </div>
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                <Upload className="w-4 h-4 text-white" />
              </div>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              accept="image/png, image/jpeg, image/gif"
              disabled={isSubmitting}
              className="hidden"
            />
            <h3 className="mt-4 text-base font-normal">Create new organization</h3>
            <p className="mt-2 text-sm text-muted-foreground text-center max-w-[200px]">
              Organizations help you manage your team and workspaces.
            </p>
          </div>

          {/* Right Panel - Form */}
          <div className="md:col-span-2 flex flex-col p-6">
            <DialogHeader>
              <DialogTitle>Create Organization</DialogTitle>
              <DialogDescription>
                Enter a name for your new organization. This will be visible to
                all members.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 flex-1 mt-6">
              <div className="space-y-2">
                <Label htmlFor="organization-name">Organization name *</Label>
                <Input
                  id="organization-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Acme Inc."
                  disabled={isSubmitting}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isSubmitting && name.trim()) {
                      handleSubmit();
                    }
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization-description">
                  Description (optional)
                </Label>
                <Textarea
                  id="organization-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this organization for?"
                  disabled={isSubmitting}
                  rows={4}
                />
              </div>
            </div>

            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              {onCancel ? (
                <Button
                  variant="ghost"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              ) : (
                <div />
              )}
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !name.trim()}
              >
                {isSubmitting ? "Creating..." : "Create Organization"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

